// SCR-STAFF-400 (فهرست) — مراجعه‌های باز شعبه و پذیرش بدون نوبت (US-BIL-001، BR-BIL-002؛ C-AGENT-552): فیلتر وضعیت و «دارای مانده»، صفحه‌بندی
// Cursor، مراجعه بدون نوبت با انتخاب مراجعه‌کننده (Alt+W) و ورود از ردیف برگه روز (/visits/by-appointment/:appointmentId).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import { unwrap, type BilVisitListItem, type BilVisitStatus, type PatientSearchItem } from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { DataTable, FormDialog, JalaliDate, ListPage, LoadingState, type DataTableColumn } from '@phoenix/ui';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { PatientPicker } from '../patients/PatientPicker';
import { useActiveBranch } from '../scheduling/common';
import { Amount, bilKeys, num, SCR_STAFF_400, visitPath, VisitStatusChip } from './common';

const STATUSES: readonly BilVisitStatus[] = ['Open', 'InService', 'AwaitingPayment', 'Reopened', 'Closed', 'Voided'];

/** پذیرش بدون نوبت: مراجعه باز امروز همان شعبه برمی‌گردد یا ساخته می‌شود (BR-BIL-002). */
export function WalkInVisitDialog({ onClose }: { onClose: () => void }) {
  const { tenantApi } = useTenantSession();
  const navigate = useNavigate();
  const { branchId } = useActiveBranch();
  const idempotency = useIdempotencyKey();
  const [patient, setPatient] = useState<PatientSearchItem | null>(null);
  const open = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/billing/visits', {
          body: { patientId: patient?.id ?? '', branchId },
          headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSettled: (_data, error) => idempotency.settle(error ?? undefined),
    onSuccess: (visit) => {
      onClose();
      void navigate(visitPath(visit.id));
    },
  });
  return (
    <FormDialog
      open
      titleKey="bil:visits.new"
      screenId={SCR_STAFF_400}
      onSubmit={() => patient && open.mutate()}
      onClose={onClose}
      isDirty={patient !== null}
      isSubmitting={open.isPending}
      submitError={open.error}
      submitKey="bil:visits.open"
    >
      <PatientPicker labelKey="bil:visits.patient" value={patient} onChange={setPatient} required testId="bil-walkin-patient" />
    </FormDialog>
  );
}

export function VisitsPage() {
  const { t } = useTranslation('bil');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const navigate = useNavigate();
  const [status, setStatus] = useState<BilVisitStatus | 'active'>('active');
  const [withBalance, setWithBalance] = useState(false);
  const [creating, setCreating] = useState(false);
  const canManage = access.accessMode === 'Full' && access.can('bil.visit.manage');

  useEffect(() => {
    if (!canManage) {
      return undefined;
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.altKey && (event.key === 'w' || event.key === 'W' || event.code === 'KeyW')) {
        event.preventDefault();
        setCreating(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canManage]);

  const filter = `${status}|${withBalance}`;
  const visits = useInfiniteQuery({
    queryKey: bilKeys.visits(session?.tenantId, filter),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        tenantApi.GET('/api/v1/billing/visits', {
          params: {
            query: {
              status: status === 'active' ? undefined : status,
              openOnly: status === 'active' ? true : undefined,
              withBalance: withBalance || undefined,
              cursor: pageParam,
              limit: 50,
            },
          },
        }),
      ),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const columns: DataTableColumn<BilVisitListItem>[] = [
    { id: 'date', headerKey: 'bil:visits.columns.date', cell: (row) => <JalaliDate value={row.openedAt} withTime /> },
    { id: 'patient', headerKey: 'bil:visits.columns.patient', cell: (row) => row.patientName ?? '—' },
    { id: 'lines', headerKey: 'bil:visits.columns.lines', cell: (row) => num(row.lineCount).toLocaleString('fa-IR') },
    { id: 'payable', headerKey: 'bil:visits.columns.payable', cell: (row) => <Amount money={row.payable} /> },
    { id: 'balance', headerKey: 'bil:visits.columns.balance', cell: (row) => <Amount money={row.balance} /> },
    { id: 'status', headerKey: 'bil:visits.columns.status', cell: (row) => <VisitStatusChip status={row.status} /> },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_400}
      titleKey="bil:visits.title"
      subtitleKey="bil:visits.subtitle"
      actions={
        canManage ? (
          <Button variant="contained" onClick={() => setCreating(true)} data-testid="bil-visit-new">
            {t('bil:visits.new')} (Alt+W)
          </Button>
        ) : undefined
      }
      filters={
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            select
            size="small"
            label={t('bil:visits.columns.status')}
            value={status}
            onChange={(event) => setStatus(event.target.value as BilVisitStatus | 'active')}
            sx={{ minWidth: 200 }}
            slotProps={{ htmlInput: { 'data-testid': 'bil-visits-status' } }}
          >
            <MenuItem value="active">{t('bil:visits.active')}</MenuItem>
            {STATUSES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`bil:visit_status.${value}`)}
              </MenuItem>
            ))}
          </TextField>
          <FormControlLabel
            control={<Switch checked={withBalance} onChange={(event) => setWithBalance(event.target.checked)} />}
            label={t('bil:visits.with_balance')}
          />
        </Stack>
      }
    >
      <DataTable
        captionKey="bil:visits.title"
        columns={columns}
        rows={visits.data?.pages.flatMap((page) => page.items)}
        getRowId={(row) => row.id}
        isLoading={visits.isPending}
        error={visits.error}
        onRetry={() => void visits.refetch()}
        hasMore={visits.hasNextPage}
        onLoadMore={() => void visits.fetchNextPage()}
        isFetchingMore={visits.isFetchingNextPage}
        emptyTitleKey="bil:visits.empty"
        onRowClick={(row) => void navigate(visitPath(row.id))}
      />
      {creating ? <WalkInVisitDialog onClose={() => setCreating(false)} /> : null}
    </ListPage>
  );
}

/** ورود از نوبت (برگه روز): مراجعه‌ای که نوبت در آن است. */
export function VisitByAppointmentPage() {
  const { t } = useTranslation('bil');
  const { appointmentId = '' } = useParams();
  const { tenantApi, session } = useTenantSession();
  const found = useQuery({
    queryKey: bilKeys.byAppointment(session?.tenantId, appointmentId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/billing/visits', { params: { query: { appointmentId, limit: 1 } } })),
    enabled: appointmentId !== '',
  });
  if (found.isPending) {
    return <LoadingState rows={3} />;
  }
  const visit = found.data?.items[0];
  if (visit) {
    return <Navigate to={visitPath(visit.id)} replace />;
  }
  return (
    <Alert severity="info" data-screen-id={SCR_STAFF_400} data-testid="bil-visit-not-found">
      {t('bil:visits.not_found')}
    </Alert>
  );
}
